import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Box, Tab, Tabs } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DashboardStats from '../../components/AdminDashboard/DashboardStats';
import UserManagement from '../../components/AdminDashboard/UserManagement';
import ConversationReview from '../../components/AdminDashboard/ConversationReview';
import SuggestedQuestions from '../../components/AdminDashboard/SuggestedQuestions';
import useAuthStore from '../../store/authStore';
import { AdminPageWrapper, DashboardContainer } from '../../components/AdminDashboard/AdminStyles';

const StyledTabs = styled(Tabs)`
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.5);
  padding: 6px;
  border-radius: 16px;
  display: inline-flex;
  width: 100%;
  box-sizing: border-box;

  & .MuiTabs-indicator {
    display: none;
  }

  & .MuiTabs-flexContainer {
    gap: 8px;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;
    padding: 4px;
    border-radius: 12px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    & .MuiTabs-flexContainer {
      gap: 4px;
    }
  }
`;

const StyledTab = styled(Tab)`
  text-transform: none;
  font-weight: 600;
  font-size: 0.95rem;
  font-family: 'Inter', sans-serif;
  color: #6b7280;
  min-height: 48px;
  border-radius: 12px;
  padding: 0 24px;
  transition: all 0.3s ease;
  z-index: 1;
  white-space: nowrap;

  &.Mui-selected {
    color: #ffffff;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }

  &:hover:not(.Mui-selected) {
    background: rgba(255, 255, 255, 0.8);
    color: #4f46e5;
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
    min-height: 40px;
    padding: 0 16px;
    border-radius: 8px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const HeaderSubtitle = styled.p`
  color: var(--text-secondary);
  margin-top: 8px;
  font-size: 1rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
    margin-top: 4px;
  }
`;

// Helper components for tabs
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ animation: 'fadeIn 0.3s ease-out' }}>{children}</Box>}
    </div>
  );
}

const AdminDashboardPage: React.FC = () => {
  const [value, setValue] = useState(0);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.is_staff) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (!user?.is_staff) return null;

  return (
    <AdminPageWrapper>
      <DashboardContainer>
        <Box
          sx={{
            mb: { xs: 3, md: 5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'end',
          }}
        >
          <div>
            <HeaderTitle>Admin Panel</HeaderTitle>
            <HeaderSubtitle>
              Dobrodošli nazaj, {user.username}. Upravljajte svojo aplikacijo.
            </HeaderSubtitle>
          </div>
        </Box>

        <Box sx={{ width: '100%' }}>
          <StyledTabs
            value={value}
            onChange={handleChange}
            aria-label="Admin Dashboard Tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <StyledTab label="📊 Pregled" />
            <StyledTab label="👥 Uporabniki" />
            <StyledTab label="💬 Pogovori" />
            <StyledTab label="❓ Vprašanja" />
          </StyledTabs>

          <TabPanel value={value} index={0}>
            <DashboardStats />
          </TabPanel>
          <TabPanel value={value} index={1}>
            <UserManagement />
          </TabPanel>
          <TabPanel value={value} index={2}>
            <ConversationReview />
          </TabPanel>
          <TabPanel value={value} index={3}>
            <SuggestedQuestions />
          </TabPanel>
        </Box>
      </DashboardContainer>
    </AdminPageWrapper>
  );
};

export default AdminDashboardPage;
